/**
 * Assignment Card Component
 *
 * Displays a single worker assignment with logs and completion status
 */

import React from 'react'
import { Check } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { getDoc, getClientId } from 'src/store/todo'
import type {
  WorkerAssignment,
  WorkerAssignmentLog,
  Worker,
  TaskType,
  Zone,
  Location,
} from 'src/store/todo/types'
import {
  getTaskColor,
  isValueTypeTask,
  getAssignmentLogs,
  getAssignmentTotalValue,
  formatTime,
  formatNumber,
} from '../../utils'

type AssignmentCardProps = {
  /** The worker assignment */
  assignment: WorkerAssignment
  /** The worker */
  worker: Worker
  /** The task type */
  task: TaskType
  /** The zone */
  zone: Zone
  /** All task types for color mapping */
  tasks: TaskType[]
  /** Map of task IDs to TaskType objects */
  tasksMap: Record<string, TaskType>
  /** All worker assignment logs */
  logs: WorkerAssignmentLog[]
  /** All locations */
  locations: Location[]
  /** Currently selected team ID */
  selectedTeamId: string
  /** Array of harvest task IDs */
  harvestTaskIds: string[]
  /** Callback to open record modal */
  onOpenRecordModal: (
    workerId: string,
    taskTypeId: string,
    zoneId: string,
    locationId?: string
  ) => void
  /** Callback to open done modal for zone-level assignments */
  onOpenDoneModal: (
    assignmentId: string,
    workerId: string,
    workerName: string,
    taskTypeId: string,
    taskName: string,
    zoneId: string,
    zoneName: string
  ) => void
  /** Callback to open summary modal showing all harvests */
  onOpenSummaryModal: (workerId: string, workerName: string, harvestTaskIds: string[]) => void
  /** Callback to set delete confirm ID */
  onSetDeleteConfirm: (logId: string) => void
  /** Synced store state */
  state: any
}

/**
 * AssignmentCard component showing a single assignment with actions
 */
const AssignmentCard: React.FC<AssignmentCardProps> = ({
  assignment,
  worker,
  task,
  zone,
  tasks,
  tasksMap,
  logs,
  locations,
  selectedTeamId,
  harvestTaskIds,
  onOpenRecordModal,
  onOpenDoneModal,
  onOpenSummaryModal,
  onSetDeleteConfirm,
  state,
}) => {
  const taskColor = getTaskColor(assignment.taskTypeId, tasks)
  const isValueTask = isValueTypeTask(assignment.taskTypeId, tasksMap)
  const assignmentLogs = getAssignmentLogs(
    logs,
    assignment.workerId,
    assignment.taskTypeId,
    assignment.zoneId,
    assignment.locationId
  )
  const totalValue = getAssignmentTotalValue(
    logs,
    assignment.workerId,
    assignment.taskTypeId,
    assignment.zoneId,
    assignment.locationId
  )

  // Get completion logs for zone-level assignments (non-value tasks)
  const completionLogs =
    !isValueTask && !assignment.locationId
      ? state.workerAssignmentLogs.filter(
          (log: any) =>
            log.workerId === assignment.workerId &&
            log.taskTypeId === assignment.taskTypeId &&
            log.zoneId === assignment.zoneId &&
            log.locationId &&
            !log.softDeleted
        )
      : []

  // Check for zone-level completion (no locationId)
  const zoneCompletion =
    !isValueTask && !assignment.locationId
      ? state.workerAssignmentLogs.find(
          (log: any) =>
            log.workerId === assignment.workerId &&
            log.taskTypeId === assignment.taskTypeId &&
            log.zoneId === assignment.zoneId &&
            !log.locationId &&
            !log.softDeleted
        )
      : null

  const hasCompletions = completionLogs.length > 0 || !!zoneCompletion

  // Check if there's a completion log for location-specific assignments
  const completionLog = assignment.locationId
    ? state.workerAssignmentLogs.find(
        (log: any) =>
          log.workerId === assignment.workerId &&
          log.taskTypeId === assignment.taskTypeId &&
          log.zoneId === assignment.zoneId &&
          log.locationId === assignment.locationId &&
          !log.softDeleted
      )
    : null

  const handleDoneClick = () => {
    if (!assignment.locationId) {
      // Zone-level assignment - open modal
      onOpenDoneModal(
        assignment.id,
        assignment.workerId,
        `${worker.firstName} ${worker.lastName}`,
        assignment.taskTypeId,
        task.taskType,
        assignment.zoneId,
        zone?.zoneMoniker || assignment.zoneId
      )
    } else {
      // Location-specific assignment - toggle completion
      if (completionLog) {
        // Already completed - soft delete
        const doc = getDoc()
        if (doc) {
          const index = state.workerAssignmentLogs.findIndex((l: any) => l.id === completionLog.id)
          if (index !== -1) {
            doc.transact(() => {
              state.workerAssignmentLogs[index].softDeleted = true
              state.workerAssignmentLogs[index].softDeletedTime = Date.now()
            })
          }
        }
      } else {
        // Not completed - create log
        const doc = getDoc()
        if (doc && selectedTeamId) {
          const newLog = {
            id: uuidv4(),
            workerId: assignment.workerId,
            teamId: selectedTeamId,
            taskTypeId: assignment.taskTypeId,
            zoneId: assignment.zoneId,
            locationId: assignment.locationId,
            value: 1,
            timestamp: Date.now(),
            clientId: getClientId()?.toString() || null,
          }
          doc.transact(() => {
            state.workerAssignmentLogs.push(newLog)
          })
        }
      }
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow flex flex-col gap-3">
      <div className="flex items-stretch gap-3">
        <div className="flex flex-col items-center gap-2">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: taskColor }}
          >
            {`${worker.firstName?.[0]}${worker.lastName?.[0]}`.toUpperCase()}
          </div>
          {isValueTask && (
            <button
              onClick={() => {
                console.log('Info button clicked in assignment card')
                console.log('Worker ID:', assignment.workerId)
                console.log('Worker Name:', `${worker.firstName} ${worker.lastName}`)
                console.log('Harvest Task IDs:', harvestTaskIds)
                onOpenSummaryModal(
                  assignment.workerId,
                  `${worker.firstName} ${worker.lastName}`,
                  harvestTaskIds
                )
              }}
              className="w-6 h-6 rounded-md flex items-center justify-center text-sm bg-green-100 text-green-700 hover:bg-green-200 transition-colors flex-shrink-0"
              title="Show all harvest records (all zones & locations)"
            >
              ℹ
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">
            {worker.firstName} {worker.lastName}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
            <span className="px-2 py-1 rounded-full text-white" style={{ backgroundColor: taskColor }}>
              {task.taskType}
            </span>
            <span className="text-gray-600">{zone?.zoneMoniker}</span>
            {assignment.locationId && (
              <>
                <span className="text-gray-600">:</span>
                <span className="text-gray-600">
                  {(() => {
                    const location = locations.find((l) => l.id === assignment.locationId)
                    return location?.locationMoniker || assignment.locationId
                  })()}
                </span>
              </>
            )}
          </div>
          {isValueTask && totalValue > 0 && (
            <p className="text-xs text-gray-600 mt-1">Total: {formatNumber(totalValue)}</p>
          )}
        </div>

        {isValueTask ? (
          <button
            onClick={() =>
              onOpenRecordModal(
                assignment.workerId,
                assignment.taskTypeId,
                assignment.zoneId,
                assignment.locationId
              )
            }
            className="flex-shrink-0 px-4 py-2 font-medium rounded-lg transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200"
          >
            Record
          </button>
        ) : (
          <button
            onClick={handleDoneClick}
            className={`flex-shrink-0 px-4 py-2 font-medium rounded-lg transition-colors flex items-center gap-2 h-full ${
              completionLog
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {completionLog ? (
              <>
                <Check size={18} />
                Done
              </>
            ) : (
              'Done?'
            )}
          </button>
        )}
      </div>

      {/* Show logs for value-type tasks */}
      {isValueTask && assignmentLogs.length > 0 && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {assignmentLogs.map((log: any) => (
              <div
                key={log.id}
                className={`flex items-center justify-between text-sm p-2 bg-gray-50 rounded ${
                  log.softDeleted ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex-1">
                  <span
                    className={`font-semibold ${
                      log.softDeleted ? 'line-through text-gray-500' : 'text-gray-900'
                    }`}
                  >
                    {formatNumber(log.value)}
                  </span>
                  <span
                    className={`ml-1 ${
                      log.softDeleted ? 'line-through text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    @ {formatTime(log.timestamp)}
                  </span>
                  {log.softDeleted && log.softDeletedTime && (
                    <span className="text-red-600 ml-2">
                      (deleted {formatTime(log.softDeletedTime)})
                    </span>
                  )}
                </div>
                {!log.softDeleted && (
                  <button
                    onClick={() => onSetDeleteConfirm(log.id)}
                    className="ml-2 w-6 h-6 flex items-center justify-center rounded hover:bg-red-200 transition-colors text-red-600 hover:text-red-700 flex-shrink-0 text-lg font-bold"
                    title="Delete record"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show zone completions for zone-level assignments */}
      {!isValueTask && !assignment.locationId && hasCompletions && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {/* Zone-level completion */}
            {zoneCompletion && (
              <div className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded">
                <div className="flex-1">
                  <span className="font-semibold text-blue-900">All Zone Complete</span>
                  <span className="ml-1 text-blue-700">
                    @ {formatTime(zoneCompletion.timestamp)}
                  </span>
                </div>
              </div>
            )}

            {/* Location-specific completions grouped by timestamp */}
            {(() => {
              const grouped = completionLogs.reduce((acc: any, log: any) => {
                const key = log.timestamp
                if (!acc[key]) {
                  acc[key] = []
                }
                acc[key].push(log)
                return acc
              }, {})

              return Object.entries(grouped).map(([timestamp, logGroup]: [string, any]) => {
                const locationNames = logGroup
                  .map((log: any) => {
                    const location = locations.find((l: any) => l.id === log.locationId)
                    return location?.locationMoniker || log.locationId
                  })
                  .join(', ')

                return (
                  <div
                    key={timestamp}
                    className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded"
                  >
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900">{locationNames}</span>
                      <span className="ml-1 text-gray-600">
                        @ {formatTime(parseInt(timestamp))}
                      </span>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default AssignmentCard
